import { service } from '@ember/service';
import Model, { attr, belongsTo, hasMany, type AsyncHasMany } from '@ember-data/model';
import type Store from '@ember-data/store';
import type { Type } from '@warp-drive/core-types/symbols';
import type Flow from 'invite-sign/models/flow';
import type GroupInviteRegistration from 'invite-sign/models/group-invite-registration';
import type Location from 'invite-sign/models/location';
import type SignInField from 'invite-sign/models/sign-in-field';
import type LocalizationService from 'invite-sign/services/localization';
import formatDate from 'invite-sign/utils/format-date';
import { filterBy } from 'macro-decorators';

export default class GroupInviteLink extends Model {
  declare [Type]: 'group-invite-link';

  @service declare localization: LocalizationService;
  @service declare store: Store;

  // RELATIONSHIPS
  @belongsTo('location', { async: false, inverse: null })
  declare location: Location;

  @belongsTo('flow', { async: false, inverse: null })
  declare flow: Flow;

  @hasMany('sign-in-field', { async: true, inverse: 'groupInviteLink' })
  declare signInFields: AsyncHasMany<SignInField>;

  // ATTRIBUTES
  @attr('string')
  declare groupName: string;

  @attr('string')
  declare flowName: string;

  @attr('string')
  declare headerText: string;

  @attr('date')
  declare expectedArrivalTime: Date;

  @attr('date')
  declare expiresAt: Date;

  @attr('number')
  declare capacityLimit: number | null;

  @attr('number')
  declare remainingCapacity: number;

  @attr('boolean', { defaultValue: false })
  declare additionalGuestsEnabled: boolean;

  @attr('boolean', { defaultValue: true })
  declare active: boolean;

  @attr('boolean', { defaultValue: false })
  declare ndaEnabled: boolean;

  @attr('boolean', { defaultValue: false })
  declare photoEnabled: boolean;

  @attr('boolean', { defaultValue: false })
  declare idScanEnabled: boolean;

  @attr('boolean', { defaultValue: false })
  declare employeeScreeningFlow: boolean;

  @filterBy('signInFields', 'required')
  declare requiredFields: SignInField[];

  get formattedExpectedArrivalTime(): string | null {
    return this.expectedArrivalTime
      ? formatDate(this.expectedArrivalTime, 'PPPp', this.localization.dateFnsLocale)
      : null;
  }

  get formattedExpiresAt(): string | null {
    return this.expiresAt ? formatDate(this.expiresAt, 'PPp', this.localization.dateFnsLocale) : null;
  }

  get isExpired(): boolean {
    return this.expiresAt ? new Date() >= this.expiresAt : false;
  }

  get hasCapacity(): boolean {
    if (this.capacityLimit === null || this.capacityLimit === undefined) return true;
    return this.remainingCapacity > 0;
  }

  get isAtCapacity(): boolean {
    if (this.remainingCapacity === null || this.remainingCapacity === undefined) return false;
    return this.remainingCapacity <= 0;
  }

  get canAcceptRegistrations(): boolean {
    return this.active && !this.isExpired && !this.isAtCapacity;
  }

  get isActiveAndNotExpired(): boolean {
    return this.active && !this.isExpired;
  }

  get isInactiveOrExpired(): boolean {
    return !this.isActiveAndNotExpired;
  }

  /**
   * Creates a new GroupInviteRegistration record based on this GroupInviteLink's configuration
   * @returns {Promise<GroupInviteRegistration>} A new GroupInviteRegistration record
   */
  async createRegistration(): Promise<GroupInviteRegistration> {
    const signInFields = await this.signInFields;

    const registration = this.store.createRecord<GroupInviteRegistration>('group-invite-registration', {
      location: this.location,
      groupInviteLink: this,
      flow: this.flow,
      additionalGuestsEnabled: this.additionalGuestsEnabled,
      expectedArrivalTime: this.expectedArrivalTime,
      purposeOfVisitName: this.flowName,
    });

    // Assign the sign-in fields array directly (same pattern as Invite)
    // @ts-expect-error - Type mismatch between NativeArray and HasMany, but this works at runtime
    registration.signInFields = signInFields.slice();

    return registration;
  }
}
